import React, {useState} from 'react';
import {i18n} from '@netx/core/i18n';
import {
	faBookMedical as PlusIcon,
	faSearch as SearchIcon,
} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Avatar} from '@mui/material';
import Chip, {ChipProps, ChipSkeleton} from '@netx/core-components/Chip';
import {Tooltip} from '@netx/core-components/Tooltip';
import {createStyles, withStyles, type WithStyles} from '@mui/styles';
import type {Theme} from '@netx/core-components/Theme';

const styles = (theme: Theme) =>
	createStyles({
		icon: {
			flex: '0 0 auto',
			lineHeight: 1,
		},
		search: {
			cursor: 'pointer',
		},
	});

export type ValueChipProps = ChipProps & {
	loading?: boolean;
	onSearch?: (value: string) => void;
	uncontrolledValue?: boolean;
	value?: string;
};

const ValueChip = ({
	classes,
	loading,
	onSearch,
	uncontrolledValue,
	value,
	...props
}: WithStyles<typeof styles> & ValueChipProps) => {
	const [disableTooltip, setDisableTooltip] = useState(false);

	if (loading) return <ChipSkeleton />;
	if (value == null) return null;

	return (
		<Chip
			aria-selected="true"
			role="option"
			color="secondary"
			disableTooltip={disableTooltip}
			label={value}
			title={uncontrolledValue ? `${i18n.t('i18n.newValue')}: ${value}` : value}
			avatar={
				uncontrolledValue ? (
					<Avatar>
						<div className={classes.icon}>
							<FontAwesomeIcon icon={PlusIcon} fixedWidth />
						</div>
					</Avatar>
				) : onSearch ? (
					<Tooltip
						title={i18n.t('i18n.search')}
						onTooltipToggle={setDisableTooltip}
					>
						<Avatar
							className={classes.search}
							data-testid="search-value"
							aria-label={i18n.t('i18n.search')}
							onClick={(event) => {
								event.stopPropagation();
								event.preventDefault();
								onSearch(value);
							}}
						>
							<div className={classes.icon}>
								<FontAwesomeIcon icon={SearchIcon} fixedWidth />
							</div>
						</Avatar>
					</Tooltip>
				) : undefined
			}
			{...props}
		/>
	);
};

export default withStyles(styles)(ValueChip as any);
